import React, { useState } from 'react';
import { gql } from '@apollo/client';
import { Delete as DeleteIcon } from 'jbc-front/components/icons';
import { useNotify } from '../hooks/useNotify';
import useReactRouter from 'use-react-router';
import { useMutation, Modal, reduxForm, useQuery, LoadingPage, useAsyncTask, ASYNC_TASK_FRAGMENT } from '../components';
import styles from './DeleteEmployeeSurvey.scss';
import Button from 'jbc-front/components/Button';
import { SelectField } from 'jbc-front/components/Form';
import moment from 'moment';

const REQUEST_OPTION = gql`
  query request_option($mailType: Int!) {
    client {
      id
      mails(mailType: $mailType) {
        id
        name
        title
        content
        updatedAt
      }
      asyncTask {
        ...AsyncTaskFields
      }
    }
  }
  ${ASYNC_TASK_FRAGMENT}
`;

const DELETE_REQUEST = gql`
  mutation deleteEmployeeSurvey($input: DeleteEmployeeSurveyInput!) {
    deleteEmployeeSurvey(input: $input) {
      client {
        id
        asyncTask {
          ...AsyncTaskFields
        }
      }
    }
  }
  ${ASYNC_TASK_FRAGMENT}
`;

export const Delete = ({ selectedSurvey, employeeSurveyId, employee, onUpdated }) => {
  const { history } = useReactRouter();
  const requestOptions = useQuery(REQUEST_OPTION, {
    variables: { mailType: 3 },
    fetchPolicy: 'no-cache'
  });

  if (requestOptions.loading) {
    return <LoadingPage />;
  }

  const taskRunning = ['waiting', 'in_progress'].includes(requestOptions.data.client.asyncTask?.status);
  const mails = requestOptions.data.client.mails;

  const mailOptions = mails.map(mail => {
    return {
      label: mail.name,
      value: mail.id
    };
  });

  return (
    <Modal>
      {({ hideModal, showModal, isOpen }) => {
        const { taskRunningProps } = useAsyncTask();
        const [selectedMail, setSelectedMail] = useState(mailOptions[0].value);
        const {
          mutation,
          value: { loading }
        } = useMutation(DELETE_REQUEST);
        const notify = useNotify();
        const sendForm = async () => {
          if (selectedMail) {
            await mutation({
              variables: {
                input: {
                  employeeSurveyId: parseInt(employeeSurveyId, 10),
                  commonId: parseInt(employee.commonId, 10),
                  mailId: parseInt(selectedMail, 10)
                }
              }
            });
            hideModal();
            onUpdated();
            history.push(`/personal_results/${selectedSurvey.id}`);
          } else {
            notify('メール内容を選択してください', 'error');
          }
        };
        return (
          <>
            <div className={styles.deleteButton}>
              <DeleteIcon size={20} className={styles.icon} onClick={() => showModal()} />
              <div className={styles.note}>実施履歴を削除</div>
            </div>
            {isOpen && (
              <form>
                <Modal.Modal>
                  <div className={styles.modal}>
                    <Modal.Header hideModal={hideModal}>個人結果の削除</Modal.Header>
                    <Modal.Body>
                      <div className={styles.modalBody}>
                        <div className={styles.surveyItem}>
                          <p className={styles.warningText}>削除した個人結果は復元することができません。</p>
                          <p className={styles.warningText}>削除すると集団分析の集計対象から除外されます。</p>
                          <p>個人結果を削除してよろしいですか？</p>
                        </div>
                        <div className={styles.surveyItem}>
                          <div>調査フォーム</div>
                          <div>{selectedSurvey.surveyForm.title}</div>
                        </div>
                        <div className={styles.surveyItem}>
                          <div>実施期間</div>
                          <div>{moment(selectedSurvey.surveyEndDate).format('YYYY年MM月DD日')}まで</div>
                        </div>
                        <div className={styles.surveyItem}>
                          <div>従業員</div>
                          <div className={styles.employeeName}>
                            {employee.lastName} {employee.firstName}
                          </div>
                        </div>
                        <div className={styles.mailField}>
                          <SelectField
                            input={{
                              value: selectedMail,
                              onChange: value => {
                                setSelectedMail(value);
                              }
                            }}
                            label="メール内容"
                            name="Mail"
                            simpleValue
                            required
                            clearable={false}
                            options={mailOptions}
                          />
                        </div>
                      </div>
                    </Modal.Body>
                    <Modal.Footer>
                      <div className={styles.modalFooter}>
                        <Modal.Buttons>
                          <Button className={styles.cancelButton} onClick={hideModal}>
                            キャンセル
                          </Button>
                          <Button
                            className={styles.sendButton}
                            primary
                            {...taskRunningProps}
                            onClick={sendForm}
                            disabledReason={taskRunning ? 'ストレスチェック送信中のため送信できません' : null}
                            disabled={!selectedMail || loading || taskRunning}>
                            削除
                          </Button>
                        </Modal.Buttons>
                      </div>
                    </Modal.Footer>
                  </div>
                </Modal.Modal>
              </form>
            )}
          </>
        );
      }}
    </Modal>
  );
};

export const DeleteForm = reduxForm({
  form: 'DeleteForm',
  enableReinitialize: true
})(Delete);
